import React from 'react';
import { Button, Card } from 'react-bootstrap';
import './StudentPositions.css';
import { downloadBlob } from '@/shared/utils/downloadBlob';
import { formatAnswerBy } from '@/shared/utils/deadline';
import {
    formatInterviewWhen,
    interviewIcs,
    nextSteps,
    positionShortName,
} from '@/shared/utils/studentPositions';

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/**
 * What the student does next, before anything else on the page: the offers to
 * answer, earliest deadline first, and the interviews still to come. Nothing
 * renders when there is neither; the rows below carry every other state.
 */
function StudentNextStepCard({ candidacies, onReviewOffer, now }) {
    const { offers, interviews } = nextSteps(candidacies, now);
    if (offers.length === 0 && interviews.length === 0) return null;
    const earliest = offers[0]?.offer?.respondBy;

    const addToCalendar = (candidacy) =>
        downloadBlob(
            new Blob([interviewIcs(candidacy)], {
                type: 'text/calendar;charset=utf-8',
            }),
            'interview.ics'
        );

    return (
        <Card
            as="section"
            className="border-0 shadow-sm mb-4"
            aria-labelledby="next-step-heading"
        >
            <Card.Body>
                <h2 id="next-step-heading" className="h5">
                    {offers.length > 0
                        ? `You have ${plural(offers.length, 'offer', 'offers')} to answer`
                        : 'Your next interview'}
                </h2>
                {earliest && (
                    <p className="mb-2">
                        Answer by {formatAnswerBy(earliest)}.
                    </p>
                )}
                <ul className="list-unstyled mb-0">
                    {offers.map((candidacy) => (
                        <li
                            key={candidacy.id}
                            className="d-flex flex-wrap align-items-center gap-2 py-1"
                        >
                            <span className="flex-grow-1">
                                {positionShortName(candidacy.position)}
                            </span>
                            <Button
                                variant="primary"
                                size="sm"
                                className="position-action"
                                onClick={() => onReviewOffer(candidacy.id)}
                            >
                                Review offer
                                <span className="visually-hidden">
                                    {' '}
                                    for {positionShortName(candidacy.position)}
                                </span>
                            </Button>
                        </li>
                    ))}
                    {interviews.map((candidacy) => (
                        <li
                            key={candidacy.id}
                            className="d-flex flex-wrap align-items-center gap-2 py-1"
                        >
                            <span className="flex-grow-1">
                                Interview for{' '}
                                {positionShortName(candidacy.position)},{' '}
                                {formatInterviewWhen(candidacy.interview.when)}
                                {candidacy.interview.where
                                    ? `, ${candidacy.interview.where}`
                                    : ''}
                            </span>
                            <Button
                                variant="outline-primary"
                                size="sm"
                                className="position-action"
                                onClick={() => addToCalendar(candidacy)}
                            >
                                Add to calendar
                                <span className="visually-hidden">
                                    {' '}
                                    for {positionShortName(candidacy.position)}
                                </span>
                            </Button>
                        </li>
                    ))}
                </ul>
            </Card.Body>
        </Card>
    );
}

export default StudentNextStepCard;
